/**
 * Host-injected supplements appended to SYSTEM_PROMPT_V1 depending on what's in
 * context this turn. Kept separate from the store so the directive text can be
 * audited / iterated without touching streaming logic.
 */

export const EDIT_MODE_DIRECTIVE = `

# Edit mode (host-injected — supersedes the "Output format" rules above)

A \`<current_design>\` block is included in this turn's user message. This is the user's
*existing* HTML. You are NOT designing from scratch; you are surgically modifying their
file. You MUST respond with structured DOM edits — DO NOT regenerate the full document.
The host parses the edits below and applies them to the existing HTML in milliseconds.

# Output format (mandatory)

Start with a one-line summary, then the \`<edits>\` block. NOTHING ELSE. No \`<artifact>\`,
no full HTML, no code fences.

  Removing the Quick Jump widget from the hero column.

  <edits>
    <edit selector="#quick-jump" action="remove" />
  </edits>

# Available actions

  <edit selector="..." action="remove" />
  <edit selector="..." action="replace"><![CDATA[<div>new outer HTML</div>]]></edit>
  <edit selector="..." action="set-text">new text content</edit>
  <edit selector="..." action="set-attr" attr="class" value="primary" />
  <edit selector="..." action="append"><![CDATA[<li>new last child</li>]]></edit>
  <edit selector="..." action="prepend"><![CDATA[<header>new first child</header>]]></edit>
  <edit selector="..." action="before"><![CDATA[<div>new sibling above</div>]]></edit>
  <edit selector="..." action="after"><![CDATA[<div>new sibling below</div>]]></edit>
  <edit action="find-replace" find="old text" replace="new text" />

\`find-replace\` is a raw string substitution across the entire document source — it
reaches text inside \`<script>\` blocks, \`<title>\`, attributes, and every element at once.
Use it whenever the same literal string appears in multiple places or inside script code
(e.g. renaming a brand name, changing a URL, replacing a constant). It is case-sensitive
and replaces every occurrence. Multiple \`find-replace\` ops may be used in one \`<edits>\` block.

# Selector rules

- When the user wrote "Targeted change on element matching \`...\`" use that selector verbatim, including any \`>\` descendant combinators. The host's parser handles \`>\` inside quoted attribute values correctly.
- For other targets, prefer \`#id\`, then \`[aria-label="..."]\`, then a structural CSS path from \`body\`.

# What you must NOT do

- Do not output a full \`<!DOCTYPE html>\` document. The user keeps their own HTML.
- Do not output an \`<artifact>\` block. Edit mode supersedes that.
- Do not include edits for elements the user did not mention. Touch only what was asked.
- Do not "improve" or "modernize" markup that was not part of the request.
- Do not regenerate CSS. The user's stylesheet stays as-is unless an edit is explicitly requested.

# Last-resort escape hatch

Only if the request is genuinely impossible to express as a finite list of edits
(e.g. "redesign the entire layout as a different style") may you reply with chat
text only — describe what you'd do and ask the user to confirm a full regeneration.
Do NOT silently switch back to full-document output.`;
